import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './ShopPage.css';

export interface ModalConfig {
  isOpen: boolean;
  type: 'info' | 'error' | 'confirm' | 'success';
  title: string;
  message: React.ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const ShopPage: React.FC = () => {
  const { user, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [modalConfig, setModalConfig] = useState<ModalConfig>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const closeModal = () =>
    setModalConfig((prev) => ({ ...prev, isOpen: false }));

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isSuccess = params.get('success') === 'true';

    if (isSuccess && user) {
      const preCheckoutPlan = localStorage.getItem('valuera_pre_checkout_plan');

      // If the plan has already updated, clear loader and URL parameters immediately
      if (preCheckoutPlan && user.planTier !== preCheckoutPlan) {
        setIsProcessingPayment(false);
        localStorage.removeItem('valuera_pre_checkout_plan');
        window.history.replaceState({}, '', '/shop');
        return;
      }

      // Otherwise, start the processing loader and fetch profile updates
      if (!isProcessingPayment) {
        setIsProcessingPayment(true);
        refreshProfile();
      }
    }
  }, [user, isProcessingPayment, refreshProfile]);

  useEffect(() => {
    if (isProcessingPayment) {
      const interval = setInterval(() => {
        refreshProfile();
      }, 2000);

      const timeout = setTimeout(() => {
        setIsProcessingPayment(false);
        localStorage.removeItem('valuera_pre_checkout_plan');
        window.history.replaceState({}, '', '/shop');
      }, 15000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [isProcessingPayment, refreshProfile]);

  // Invoices fetch removed

  const handlePlanAction = async (tier: string) => {
    try {
      if (tier === 'free') {
        setModalConfig({
          isOpen: true,
          type: 'confirm',
          title: 'Abo kündigen',
          message:
            'Möchtest du dein Abonnement wirklich kündigen? Du behältst den Zugriff auf deine Premium-Features bis zum Ende des aktuellen Abrechnungszeitraums, danach wird dein Account auf die kostenlose Version zurückgestuft.',
          onConfirm: async () => {
            closeModal();
            setLoading(true);
            try {
              await api.post('/billing/cancel-subscription');
              setModalConfig({
                isOpen: true,
                type: 'success',
                title: 'Erfolgreich',
                message:
                  'Dein Abonnement wurde erfolgreich zum Ende des aktuellen Abrechnungszeitraums gekündigt.',
                onConfirm: () => window.location.reload(),
              });
            } catch (error) {
              console.error('Cancel failed', error);
              setModalConfig({
                isOpen: true,
                type: 'error',
                title: 'Fehler',
                message: (
                  <>
                    Aktion fehlgeschlagen. Bitte versuche es später noch einmal.
                    Sollte das Problem weiterhin bestehen, kontaktiere uns bitte
                    unter{' '}
                    <a href="mailto:office@valuera.at">office@valuera.at</a>.
                  </>
                ),
              });
              setLoading(false);
            }
          },
          onCancel: closeModal,
        });
      } else {
        const isDowngrade = currentTier === 'pro' && tier === 'light';

        if (isDowngrade) {
          setModalConfig({
            isOpen: true,
            type: 'info',
            title: 'Weiterleitung zum Kundenportal',
            message:
              'Um auf den Light-Plan zu wechseln, wirst du nun zum Kundenportal weitergeleitet. Bitte wähle dort "Plan ändern" aus.',
            onConfirm: async () => {
              closeModal();
              setLoading(true);
              try {
                const response = await api.post('/billing/portal-session');
                if (response.data?.data?.url) {
                  window.location.href = response.data.data.url;
                } else {
                  throw new Error('Keine Portal-URL erhalten.');
                }
              } catch (error) {
                console.error('Portal failed', error);
                setModalConfig({
                  isOpen: true,
                  type: 'error',
                  title: 'Fehler',
                  message: (
                    <>
                      Aktion fehlgeschlagen. Bitte versuche es später noch
                      einmal. Sollte das Problem weiterhin bestehen, kontaktiere
                      uns bitte unter{' '}
                      <a href="mailto:office@valuera.at">office@valuera.at</a>.
                    </>
                  ),
                });
                setLoading(false);
              }
            },
          });
          return;
        }

        setLoading(true);
        localStorage.setItem(
          'valuera_pre_checkout_plan',
          user?.planTier || 'free',
        );
        const response = await api.post('/billing/checkout-session', { tier });
        if (response.data?.data?.url) {
          window.location.href = response.data.data.url;
        } else {
          localStorage.removeItem('valuera_pre_checkout_plan');
          throw new Error('Keine Weiterleitungs-URL von Stripe erhalten.');
        }
      }
    } catch (error) {
      console.error('Plan action failed', error);
      setModalConfig({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: (
          <>
            Aktion fehlgeschlagen. Bitte versuche es später noch einmal. Sollte
            das Problem weiterhin bestehen, kontaktiere uns bitte unter{' '}
            <a href="mailto:office@valuera.at">office@valuera.at</a>.
          </>
        ),
      });
      setLoading(false);
    }
  };

  const currentTier = user?.planTier || 'free';

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="shop-page">
      {isProcessingPayment && (
        <div className="payment-processing-banner">
          <div className="spinner"></div>
          <span>
            Zahlung wird verarbeitet. Dein Profil wird in Kürze aktualisiert...
          </span>
        </div>
      )}
      <h1>Subscribtions</h1>
      <p>Wähle den passenden Plan für deine Immobilien.</p>

      <div className="pricing-grid">
        {/* FREE */}
        <div
          className={`pricing-card ${currentTier === 'free' ? 'active' : ''}`}
        >
          <h2>Free</h2>
          {user?.pendingPlanTier === 'free' && (
            <div className="pending-badge">
              Geplant ab {formatDate(user.currentPeriodEnd)}
            </div>
          )}
          <div className="pricing-price">
            0,00 € <span>/ Monat</span>
          </div>
          <ul className="pricing-features">
            <li>1 Immobilie</li>
            <li>1 Bewertung</li>
            <li>50 MB Speicherplatz</li>
            <li>6 Monate Marktdaten-Historie</li>
          </ul>
          <button
            disabled={
              loading ||
              currentTier === 'free' ||
              user?.pendingPlanTier === 'free'
            }
            onClick={() => handlePlanAction('free')}
          >
            {currentTier === 'free'
              ? 'Dein aktueller Plan'
              : user?.pendingPlanTier === 'free'
                ? 'Kündigung vorgemerkt'
                : 'Downgrade auf Free'}
          </button>
        </div>

        {/* LIGHT */}
        <div
          className={`pricing-card ${currentTier === 'light' ? 'active' : ''}`}
        >
          <h2>Light</h2>
          {user?.pendingPlanTier === 'light' && (
            <div className="pending-badge">
              Geplant ab {formatDate(user.currentPeriodEnd)}
            </div>
          )}
          <div className="pricing-price">
            4,99 € <span>/ Monat</span>
          </div>
          <ul className="pricing-features">
            <li>Bis zu 3 Immobilien</li>
            <li>Unbegrenzte Bewertungen</li>
            <li>1 GB Speicherplatz</li>
            <li>1 Jahr Marktdaten-Historie</li>
          </ul>
          <button
            disabled={
              loading ||
              currentTier === 'light' ||
              user?.pendingPlanTier === 'light'
            }
            onClick={() => handlePlanAction('light')}
          >
            {currentTier === 'light'
              ? 'Dein aktueller Plan'
              : user?.pendingPlanTier === 'light'
                ? 'Wechsel vorgemerkt'
                : currentTier === 'free'
                  ? 'Upgrade auf Light'
                  : 'Wechseln zu Light'}
          </button>
        </div>

        {/* PRO */}
        <div
          className={`pricing-card ${currentTier === 'pro' ? 'active' : ''}`}
        >
          <h2>Pro</h2>
          {user?.pendingPlanTier === 'pro' && (
            <div className="pending-badge">
              Geplant ab {formatDate(user.currentPeriodEnd)}
            </div>
          )}
          <div className="pricing-price">
            12,99 € <span>/ Monat</span>
          </div>
          <ul className="pricing-features">
            <li>Bis zu 10 Immobilien</li>
            <li>Unbegrenzte Bewertungen</li>
            <li>5 GB Speicherplatz</li>
            <li>5 Jahre Marktdaten-Historie</li>
          </ul>
          <button
            disabled={
              loading ||
              currentTier === 'pro' ||
              user?.pendingPlanTier === 'pro'
            }
            onClick={() => handlePlanAction('pro')}
          >
            {currentTier === 'pro'
              ? 'Dein aktueller Plan'
              : user?.pendingPlanTier === 'pro'
                ? 'Wechsel vorgemerkt'
                : currentTier === 'free'
                  ? 'Upgrade auf Pro'
                  : 'Wechseln zu Pro'}
          </button>
        </div>

        {/* BUSINESS */}
        <div
          className={`pricing-card ${currentTier === 'business' ? 'active' : ''}`}
        >
          <h2>Business</h2>
          {user?.pendingPlanTier === 'business' && (
            <div className="pending-badge">
              Geplant ab {formatDate(user.currentPeriodEnd)}
            </div>
          )}
          <div className="pricing-price">
            49,99 € <span>/ Monat</span>
          </div>
          <ul className="pricing-features">
            <li>Unbegrenzte Immobilien</li>
            <li>Unbegrenzte Bewertungen</li>
            <li>20 GB Speicherplatz</li>
            <li>10 Jahre Marktdaten-Historie</li>
          </ul>
          <button
            disabled={
              loading ||
              currentTier === 'business' ||
              user?.pendingPlanTier === 'business'
            }
            onClick={() => handlePlanAction('business')}
          >
            {currentTier === 'business'
              ? 'Dein aktueller Plan'
              : user?.pendingPlanTier === 'business'
                ? 'Wechsel vorgemerkt'
                : currentTier === 'free'
                  ? 'Upgrade auf Business'
                  : 'Wechseln zu Business'}
          </button>
        </div>
      </div>

      {modalConfig.isOpen && (
        <div
          className="confirm-modal-overlay"
          onClick={
            modalConfig.type !== 'confirm'
              ? modalConfig.onConfirm || closeModal
              : undefined
          }
        >
          <div
            className="confirm-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>{modalConfig.title}</h3>
            <p>{modalConfig.message}</p>
            <div className="confirm-modal-actions">
              {modalConfig.type === 'confirm' && (
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={modalConfig.onCancel || closeModal}
                >
                  Abbrechen
                </button>
              )}
              {modalConfig.type === 'confirm' ? (
                <button
                  type="button"
                  className="btn-danger"
                  onClick={modalConfig.onConfirm}
                >
                  Kündigen
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={modalConfig.onConfirm || closeModal}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopPage;
